import { getCurrencyByLocale } from "../../utils/translator";
import { CarePlusPlanDuration } from "../ChoosePlanCarePlus/types";

// Crossed-out price: a price is struck through only where a real discount exists, and
// wherever one is struck through the anchor note must appear (AC-4). Two surfaces answer that
// question — the card that draws the strike-through and the page that draws the note — so both read
// it from here. Written twice, they can drift and leave a note with nothing crossed out.

/** A plan duration is discounted when its bonus actually lowers the monthly fee. */
export const hasPlanDiscount = (duration: CarePlusPlanDuration | null | undefined): boolean => {
  if (!duration?.bonus || duration.bonus.discount <= 0) return false;
  return duration.charged_fee < duration.total_fee;
};

/** A one-off purchase is discounted when the bonus leaves less than the full price. */
export const hasOneTimeDiscount = (price: number, chargedPrice: number): boolean =>
  price > 0 && chargedPrice < price;

/**
 * The price shown must match what Checkout charges: every price on this page is a
 * sum of DB values, and a missing one reads as 0 — indistinguishable from a deliberate zero. That
 * would advertise a cheaper option than checkout will charge, so an option whose price is not fully
 * known is not offered at all rather than offered wrong.
 *
 * A fee that is genuinely waived arrives as a BONUS on top of the full price (charged_fee,
 * one_time_bonus), never as a missing component, so these checks cannot hide a real free month.
 */
export const isPriced = (...components: (number | null | undefined)[]): boolean =>
  components.every((value) => Number.isFinite(Number(value)) && Number(value) > 0);

/**
 * A waived amount, which the Figma writes as "0 €" rather than "0,00 €". Formatted with no decimals
 * instead of formatting 0,00 and stripping them afterwards: a regex over an already-formatted price
 * has to guess the locale's decimal separator and grouping, and cuts the wrong run when it guesses
 * wrong. Callers pass the cart locale (useCarePlusPrice), so this matches every other price here.
 */
const FREE_PRICE_LOCALE = "de-DE";
export const freePrice = (locale: string = FREE_PRICE_LOCALE): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: getCurrencyByLocale(locale, false),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(0);

/**
 * Pack size for a tablet product: "30p" rather than the catalog's "30 Tabletten".
 * Built from the dosage's numeric `quantity`, so it does not depend on the German word in the
 * localized label — matching that literally leaves every other language unshortened.
 */
export const tabletPackLabel = (source: { quantity?: number } | null | undefined): string => {
  const quantity = Number(source?.quantity ?? 0);
  return quantity > 0 ? `${quantity}p` : "";
};
